import { useInfiniteQuery, type UseInfiniteQueryResult } from '@tanstack/react-query';

import { ApiError, apiJson } from '@/lib/apiClient';

/**
 * The admin WhatsApp message log (slice 70b).
 *
 * Types mirror the wire contract in `api/src/modules/whatsapp/adminRoutes.ts`
 * (seam S-wire-tenantName / S-wire-timestamps) and are never re-declared from the
 * screen's point of view. The FULL phone never crosses the wire — only
 * `peerPhoneMasked` (`***499`) — so there is nothing here to un-mask.
 */

export interface WhatsAppMessage {
  id: string;
  /** 'outbound' (Raad → customer) or 'inbound' (customer → Raad). */
  direction: string;
  tenantId: string | null;
  /** Resolved company name, or null for an inbound number that matched no user. */
  tenantName: string | null;
  waPhoneNumberId: string;
  /** Last 3 digits — present for every row. */
  peerPhoneMasked: string;
  /** The FULL E.164 number, but ONLY for a null-tenant "Unknown sender" row (Mahad,
   * 31 Oct); null for an attributed row, whose full number never leaves the server. */
  peerPhone: string | null;
  templateName: string | null;
  templateLang: string | null;
  /** Inbound free text (the customer's reply), or null for a template-only outbound. */
  bodyText: string | null;
  wamid: string | null;
  status: string;
  errorCode: string | null;
  errorTitle: string | null;
  statusUpdatedAt: string | null;
  /** The status ladder — each the first event of that status (ISO), else null. */
  sentAt: string | null;
  deliveredAt: string | null;
  readAt: string | null;
  /** SLICE 70c — the opaque thread token this row belongs to; the log links to it. */
  conversationId: string;
  createdAt: string;
}

export interface WhatsAppMessagePage {
  messages: WhatsAppMessage[];
  /** Opaque keyset cursor fed back VERBATIM; null = no further page. */
  nextCursor: string | null;
}

export interface WhatsAppFilters {
  status?: string;
  tenantId?: string;
  direction?: string;
  limit?: number;
}

export const whatsappMessagesQueryKey = (filters: WhatsAppFilters) =>
  ['admin', 'whatsapp', 'messages', filters] as const;

export function useWhatsAppMessages(
  filters: WhatsAppFilters = {},
): UseInfiniteQueryResult<{ pages: WhatsAppMessagePage[]; pageParams: unknown[] }, Error> {
  return useInfiniteQuery({
    queryKey: whatsappMessagesQueryKey(filters),
    queryFn: ({ pageParam, signal }) => {
      const p = new URLSearchParams();
      if (filters.status) p.set('status', filters.status);
      if (filters.tenantId) p.set('tenantId', filters.tenantId);
      if (filters.direction) p.set('direction', filters.direction);
      if (filters.limit) p.set('limit', String(filters.limit));
      // The cursor is whatever the server handed back last; never reconstructed.
      if (typeof pageParam === 'string' && pageParam !== '') p.set('cursor', pageParam);
      const qs = p.toString();
      return apiJson<WhatsAppMessagePage>(
        `/api/admin/whatsapp/messages${qs ? `?${qs}` : ''}`,
        { signal },
      );
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    // A 404/4xx is a decision (this surface 404s a non-super-admin), not a hiccup —
    // retrying it just delays the answer. Mirrors useTenants.
    retry: (count, error) =>
      !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
  });
}
